import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { FlaskConical, Trophy, RefreshCw, Mail } from "lucide-react";

/**
 * Drip Subject-Line A/B Performance
 * -----------------------------------------------------------
 * Renders the rotating-subject-line experiment for the 5-stage
 * ghost-account onboarding drip. Each row shows per-(stage, lang,
 * variant) sent count, opens, open-rate %, and a gold "WINNER" pill
 * once the lock threshold (≥30 sends + ≥10pp lead) fires.
 */

const STAGE_LABELS = {
  ghost_welcome: "Welcome (day 0)",
  ghost_tour: "Tour (day 1)",
  ghost_casestudy: "Case study (day 3)",
  ghost_rescan: "Re-scan (day 7)",
  ghost_discount: "Discount (day 14)",
};

function adminHeaders() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.email ? { "X-User-Email": u.email } : {};
  } catch {
    return {};
  }
}

export default function AdminDripABTab() {
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/drip/ab-summary`, {
        headers: adminHeaders(),
      });
      setGrouped(r.data?.grouped || {});
    } catch (e) {
      console.error("drip ab-summary fetch failed", e);
    }
    setRefreshing(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="admin-root p-6 text-sm text-gray-500" data-testid="drip-ab-loading">
        Načítavam A/B štatistiky…
      </div>
    );
  }

  const stages = Object.keys(grouped);

  return (
    <div className="admin-root space-y-6" data-testid="drip-ab-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-violet-600" />
            Drip Subject A/B
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-xl">
            Rotujúce subject linky pre 5-stupňový ghost-account drip. Variant
            sa zamkne ako víťaz pri ≥30 odoslaniach a ≥10pp náskoku v open-rate.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          data-testid="drip-ab-refresh"
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Obnoviť
        </button>
      </div>

      {stages.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
          Žiadne dáta — drip campaign zatiaľ neodoslala ani jeden email.
        </div>
      ) : (
        stages.map((stage) => {
          const langs = grouped[stage] || {};
          return (
            <div
              key={stage}
              data-testid={`drip-ab-stage-${stage}`}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden"
            >
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-500" />
                <h3 className="font-bold text-gray-900 text-sm">
                  {STAGE_LABELS[stage] || stage}
                </h3>
              </div>
              {Object.entries(langs).map(([lang, variants]) => (
                <div key={lang} className="border-b border-gray-100 last:border-b-0">
                  <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-mono">
                    {lang}
                  </div>
                  <table className="w-full text-sm" data-testid={`drip-ab-table-${stage}-${lang}`}>
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                        <th className="text-left px-4 py-2 font-semibold">Variant</th>
                        <th className="text-left px-4 py-2 font-semibold">Subject</th>
                        <th className="text-right px-4 py-2 font-semibold">Sent</th>
                        <th className="text-right px-4 py-2 font-semibold">Opens</th>
                        <th className="text-right px-4 py-2 font-semibold">Rate</th>
                        <th className="text-center px-4 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((v) => (
                        <tr
                          key={v.variant_key}
                          data-testid={`drip-ab-row-${stage}-${lang}-${v.variant_key}`}
                          className={v.locked_winner ? "bg-amber-50" : ""}
                        >
                          <td className="px-4 py-2 font-bold text-gray-900">{v.variant_key}</td>
                          <td className="px-4 py-2 text-gray-700 text-xs font-mono">
                            {v.subject_template}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900">{v.sent}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900">{v.opens}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-bold text-gray-900">
                            {v.open_rate_pct}%
                          </td>
                          <td className="px-4 py-2 text-center">
                            {v.locked_winner ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase">
                                <Trophy className="w-3 h-3" />
                                Winner
                              </span>
                            ) : (
                              <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                                {v.sent < 30 ? "Collecting" : "Tied"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}
